import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { useIntl } from 'react-intl';

import styled from '@emotion/styled';
import { image } from '@/theme';

import { LOCAL_LANGUAGE } from '@/constants/storageKey';
import { ExtraBoldText, RegularText } from '@/components/text';
import { useGetBusinessCategory } from '@/hooks/CommonRequests';
import { mq } from '@/utils/mediaQuery';
import { Container } from './inquryStyles';

const TextWrapper = styled.div(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2rem 0',
  marginBottom: '2rem',
  whiteSpace: 'break-spaces',
  textAlign: 'center',
}));

const ButtonGroup = styled.div(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '2.4rem',
  justifyContent: 'center',
  width: '100%',
  maxWidth: '60rem',
  margin: '0 auto',

  [mq('tablet')]: {
    gap: '3.2rem',
  },
}));

const ModernCardButton = styled.button(({ theme }) => ({
  position: 'relative',
  display: 'block',
  width: '100%',
  height: '120px',
  padding: 0,
  border: '1px solid #eaeaea',
  borderRadius: '8px',
  cursor: 'pointer',
  backgroundColor: '#ffffff',
  overflow: 'hidden',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    border: `1px solid ${theme.color.base_purple}`,
  },
  '&:hover .banner-hover-overlay': {
    opacity: 1,
  },
  '&:hover .banner-btn': {
    opacity: 1,
    transform: 'translateX(0)',
  },
}));

const BannerCard = styled.div(() => ({
  position: 'relative',
  width: '100%',
  height: '120px',
  borderRadius: '8px',
  overflow: 'hidden',
  cursor: 'pointer',
  '&:hover .banner-hover-overlay': {
    opacity: 1,
  },
  '&:hover .banner-btn': {
    opacity: 1,
    transform: 'translateX(0)',
  },
}));

const BannerImage = styled.img(() => ({
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'cover',
}));

const BannerHoverOverlay = styled.div(() => ({
  position: 'absolute',
  inset: 0,
  backgroundColor: 'rgba(111, 92, 128, 0.6)',
  opacity: 0,
  transition: 'opacity 0.3s ease-in-out',
  zIndex: 1,
}));

const BannerContent = styled.div(() => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  padding: '0 2rem 2rem',
  zIndex: 2,

  [mq('tablet')]: { padding: '0 2.4rem 2.4rem' },
}));

// 이미지 배너용 텍스트 (흰색)
const BannerTextGroup = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '1rem',
  color: theme.color.white,
}));

// 일반 카드용 텍스트 (어두운 색)
const ModernTextGroup = styled.div(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '1rem',
  color: '#333333',
}));

const MainTitle = styled.span(() => ({
  fontSize: '1.6rem',
  fontWeight: 600,

  [mq('tablet')]: { fontSize: '2rem' },
}));

const SubTitle = styled.span(() => ({
  fontSize: '1rem',
  [mq('tablet')]: { fontSize: '1.2rem' },
}));

const InquiryButton = styled.div(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.8rem',
  padding: '0.8rem 1.6rem',
  backgroundColor: '#ffffff',
  color: '#333333',
  borderRadius: '4px',
  fontSize: '1.4rem',
  fontWeight: 600,
  opacity: 0,
  transition: 'all 0.3s ease-in-out',
  transform: 'translateX(10px)',
  '& img': {
    width: '1.6rem',
    height: '1.6rem',
  },
}));

const Inquiry = () => {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const navigate = useNavigate();
  const intl = useIntl();
  const { data: businessCategory } = useGetBusinessCategory();

  const lang = localStorage.getItem(LOCAL_LANGUAGE) || 'en';
  const baseUrl = lang !== null ? `/${lang}` : '';

  const parseCategoryName = (name) => {
    if (!name || typeof name !== 'string') return { main: '', sub: '' };

    const normalizedName = name.replace(/（/g, '(').replace(/）/g, ')');
    const splitIndex = normalizedName.indexOf('(');

    if (splitIndex !== -1) {
      const main = normalizedName.slice(0, splitIndex).trim();
      const sub = normalizedName
        .slice(splitIndex + 1)
        .replace(')', '')
        .trim();
      return { main, sub };
    }

    return { main: name.trim(), sub: '' };
  };

  const getBannerImage = (codeId) => {
    switch (codeId) {
      case 'arena_inquiry':
        return image.landingArena;
      case 'convention_facilities_inquiry':
        return image.landingMice;
      default:
        return null;
    }
  };

  return (
    <Container>
      <TextWrapper>
        <ExtraBoldText id="lang-event-inquiry" fontSize={isMobile ? '3.6rem' : '4.8rem'} />
        <RegularText id="lang-inquiry-description" secondary />
      </TextWrapper>

      <ButtonGroup>
        {businessCategory?.map((category) => {
          const bannerImg = getBannerImage(category.code_id);
          const { main, sub } = parseCategoryName(category.name);

          // 이미지가 있는 경우 (배너 스타일)
          if (bannerImg) {
            return (
              <BannerCard
                key={category.code_id}
                onClick={() => navigate(`${baseUrl}/inquiry/${category.code_id}`)}
              >
                <BannerImage src={bannerImg} alt={main} />
                <BannerHoverOverlay className="banner-hover-overlay" />

                <BannerContent>
                  <BannerTextGroup>
                    <MainTitle>{main}</MainTitle>
                    {sub && <SubTitle>{sub}</SubTitle>}
                  </BannerTextGroup>
                  <InquiryButton className="banner-btn">
                    {intl.formatMessage({ id: 'lang-inquiry' })}
                    <img src={image.arrowRightPrimary.default} alt="arrow icon" />
                  </InquiryButton>
                </BannerContent>
              </BannerCard>
            );
          }

          // 이미지가 없는 경우
          return (
            <ModernCardButton
              key={category.code_id}
              onClick={() => navigate(`${baseUrl}/inquiry/${category.code_id}`)}
            >
              <BannerHoverOverlay className="banner-hover-overlay" />

              <BannerContent>
                <ModernTextGroup>
                  <MainTitle>{main}</MainTitle>
                  {sub && <SubTitle>{sub}</SubTitle>}
                </ModernTextGroup>
                <InquiryButton className="banner-btn">
                  {intl.formatMessage({ id: 'lang-inquiry' })}
                  <img src={image.arrowRightPrimary.default} alt="arrow icon" />
                </InquiryButton>
              </BannerContent>
            </ModernCardButton>
          );
        })}
      </ButtonGroup>
    </Container>
  );
};

export default Inquiry;
